import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { peopleAPI, authAPI, userAPI } from '../services/api';
import { getProfilePicture, getUploadUrl } from '../utils/helpers';
import { toast } from 'react-toastify';
import { FaGlobe, FaFacebook, FaBars, FaTimes } from 'react-icons/fa';

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, login, isAuthenticated, loading: authLoading } = useAuth();
  const { language, changeLanguage, availableLanguages } = useLanguage();
  const [showLogin, setShowLogin] = useState(searchParams.get('login') !== null);
  const [users, setUsers] = useState([]); // For both Join Happy Couples and Featured Members
  const [usersCount, setUsersCount] = useState(0);
  const [totalUsersCount, setTotalUsersCount] = useState(null); // Will be loaded from API

  // Maximum number of featured members to show on landing page
  const maxFeaturedMembers = 9;
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const hasRedirected = useRef(false);
  const hasFetchedUsers = useRef(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    age: '',
    gender: '',
    interested_in: 'Everyone',
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  // Redirect authenticated users (only for direct access when already logged in)
  useEffect(() => {
    console.log('🏠 Landing: isAuthenticated=', isAuthenticated, 'authLoading=', authLoading, 'hasRedirected=', hasRedirected.current);
    if (isAuthenticated && !hasRedirected.current && !authLoading) {
      console.log('🏠 Landing: Redirecting to /encounters (already authenticated)');
      hasRedirected.current = true;
      navigate('/encounters', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);


  // Fetch users only once - for featured members section
  useEffect(() => {
    console.log('🏠 Landing: Checking if should fetch users...');
    if (!hasFetchedUsers.current && !authLoading) {
      console.log('🏠 Landing: Fetching random users for featured members');
      hasFetchedUsers.current = true;
      fetchRandomUsers();
    }
  }, [authLoading]);

  const fetchRandomUsers = async () => {
    try {
      console.log('🔍 Fetching featured members (same pattern as encounters)...');

      // Fetch featured members and total user count in parallel
      const [peopleResponse, countResponse] = await Promise.all([
        peopleAPI.getRandomPeople(maxFeaturedMembers),
        userAPI.getTotalUserCount().catch(err => {
          console.warn('⚠️ Could not fetch user count, using fallback:', err);
          return { success: true, total: null };
        })
      ]);

      console.log('✅ People API response:', peopleResponse);
      console.log('✅ Count API response:', countResponse);

      // Update total user count
      if (countResponse && countResponse.success && countResponse.total) {
        setTotalUsersCount(countResponse.total);
        console.log('✅ Total user count loaded:', countResponse.total);
      }

      if (peopleResponse && peopleResponse.people && peopleResponse.people.length > 0) {
        setUsers(peopleResponse.people);
        setUsersCount(peopleResponse.people.length);
        console.log('✅ Featured members loaded from database!');
      } else {
        console.log('⚠️ No users found in database');
        setUsers([]);
        setUsersCount(0);
      }
    } catch (error) {
      console.error('❌ Failed to fetch featured members:', error);
      setUsers([]);
      setUsersCount(0);
      console.log('✅ Fallback user loaded!');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await register(formData);
      if (result.success) {
        toast.success('Registration successful!');
        navigate('/wizard');
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(loginData, () => {
        toast.success('Login successful!');
        navigate('/encounters');
      });
      if (!result.success) {
        toast.error(result.error || 'Invalid credentials');
      }
    } catch (error) {
      toast.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(resetEmail);
      toast.success(response.message || 'Password reset link sent to your email');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg backdrop-blur-sm bg-opacity-95 relative z-50" role="navigation">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link className="flex items-center text-white hover:text-gray-200 font-bold text-xl transition-all duration-300" to="/">
              <img src="/assets/images/logo-matchus.svg" alt="MatchUs" className="h-8 w-8 mr-3" />
              <span className="text-2xl font-bold tracking-wide">Match Us</span>
            </Link>

            <div className="flex items-center space-x-6">
              {/* Language Selector */}
              <div className="relative z-60">
                <button
                  className="flex items-center text-white hover:text-gray-100 px-3 py-2 text-sm font-medium transition-all duration-300 rounded-md hover:bg-white hover:bg-opacity-10"
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                >
                  <FaGlobe className="mr-2" />
                  {language === 'en' ? 'EN' : 'TR'}
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-xl z-70 border border-gray-100">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 first:rounded-t-lg last:rounded-b-lg transition-colors"
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/login"
                className="text-white hover:text-gray-100 px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md hover:bg-white hover:bg-opacity-10"
              >
                Login
              </Link>
              {!showLogin ? (
                <button
                  onClick={() => setShowLogin(true)}
                  className="bg-white text-blue-600 px-6 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 hover:text-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Sign Up
                </button>
              ) : (
                <button
                  onClick={() => setShowLogin(false)}
                  className="bg-white text-blue-600 px-6 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 hover:text-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Sign Up
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>


      {/* Hero Section - PHP Site Style */}
      <header className="image-bg-fixed-height relative">
        <div
          className="flex items-center justify-center min-h-screen py-20 bg-cover bg-center bg-no-repeat relative"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${getUploadUrl('landing-background.jpeg')}')`
          }}
        >
          {/* Featured Members Overlay - Professional Stories Style */}
          <div className="absolute top-8 left-0 right-0 z-50">
            <div className="container mx-auto px-4">
              {/* Featured Members Header */}
              <div className="text-center mb-3">
                <div className="inline-flex items-center bg-black bg-opacity-40 backdrop-blur-md rounded-full px-4 py-2 border border-white border-opacity-20">
                  <span className="text-white text-sm font-semibold mr-3">✨ Featured Members</span>
                  <div className="flex -space-x-1 mr-3">
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                  </div>
                  <div className="w-px h-4 bg-white bg-opacity-30 mx-2"></div>
                  <div className="flex -space-x-2 mr-2">
                    <div className="w-4 h-4 rounded-full bg-pink-400 border border-white"></div>
                    <div className="w-4 h-4 rounded-full bg-purple-400 border border-white"></div>
                    <div className="w-4 h-4 rounded-full bg-blue-400 border border-white"></div>
                    <div className="w-4 h-4 rounded-full bg-green-400 border border-white"></div>
                    <div className="w-4 h-4 rounded-full bg-yellow-400 border border-white"></div>
                    <div className="w-4 h-4 rounded-full bg-orange-400 border border-white"></div>
                  </div>
                  <span className="text-white text-xs font-medium">
                    {totalUsersCount === null ? 'Loading...' : totalUsersCount.toLocaleString() + '+ members'}
                  </span>
                </div>
              </div>

              {/* Featured Members Carousel */}
              <div className="flex justify-center">
                <div className="bg-black bg-opacity-30 backdrop-blur-lg rounded-2xl p-4 border border-white border-opacity-10 shadow-2xl">
                  <div className="flex space-x-4 overflow-x-auto pb-2 px-2 max-w-5xl scrollbar-hide">
                    {users.length > 0 ? (
                      users.slice(0, 8).map((user, index) => {
                        const imageUrl = getProfilePicture(user, '');
                        const colors = [
                          'from-pink-400 via-rose-400 to-pink-500',
                          'from-purple-400 via-indigo-400 to-purple-500',
                          'from-blue-400 via-cyan-400 to-blue-500',
                          'from-green-400 via-emerald-400 to-green-500',
                          'from-yellow-400 via-orange-400 to-yellow-500',
                          'from-red-400 via-pink-400 to-red-500',
                          'from-indigo-400 via-purple-400 to-indigo-500',
                          'from-cyan-400 via-blue-400 to-cyan-500'
                        ];
                        const colorClass = colors[index % colors.length];

                        return (
                          <div key={user.id} className="flex-shrink-0 text-center group cursor-pointer">
                            <div className={`relative w-20 h-20 mx-auto mb-2 p-1 rounded-full bg-gradient-to-tr ${colorClass} transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-2xl`}>
                              <div className="w-full h-full bg-white bg-opacity-25 rounded-full p-1 backdrop-blur-sm border border-white border-opacity-30">
                                <img
                                  src={imageUrl}
                                  alt={user.full_name}
                                  className="w-full h-full rounded-full object-cover border-2 border-white border-opacity-70 shadow-inner"
                                  onError={(e) => {
                                    console.log('❌ Featured member image failed for', user.full_name, '- using placeholder');
                                    e.target.src = '/assets/images/blank-photo.png';
                                  }}
                                />
                              </div>
                            </div>
                            <div className="max-w-20">
                              <p className="text-xs font-bold text-white drop-shadow-lg truncate">{user.full_name.split(' ')[0]}</p>
                              <p className="text-xs text-gray-200 drop-shadow-md">{user.age} • {user.city || 'Nearby'}</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex-shrink-0 text-center">
                        <div className="relative w-20 h-20 mx-auto mb-2 p-1 rounded-full bg-gradient-to-tr from-gray-500 to-gray-600">
                          <div className="w-full h-full bg-white bg-opacity-20 rounded-full p-1 backdrop-blur-sm flex items-center justify-center border border-white border-opacity-30">
                            <span className="text-gray-300 text-lg">👤</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-300 font-medium">Loading...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="container mx-auto px-4 relative z-20 mt-32 lg:mt-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Find Your Perfect Match
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Join millions of people finding love and meaningful connections.
                </p>
              </div>

              <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto lg:mx-0 mt-8">
                <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 relative z-30">
                  {!showLogin ? (
                    <>
                      <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-text mb-2">Sign Up</h2>
                        <div className="w-full h-px bg-gray-200"></div>
                      </div>

                      <form onSubmit={handleRegister}>
                        <input
                          type="text"
                          name="full_name"
                          placeholder="Full Name"
                          className="form-control mb-4"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          required
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email"
                          className="form-control mb-4"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                        <input
                          type="password"
                          name="password"
                          placeholder="Password"
                          className="form-control mb-4"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                        <select
                          name="age"
                          className="form-control mb-4"
                          value={formData.age}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Age</option>
                          {Array.from({ length: 83 }, (_, i) => i + 18).map((age) => (
                            <option key={age} value={age}>{age}</option>
                          ))}
                        </select>
                        <select
                          name="gender"
                          className="form-control mb-6"
                          value={formData.gender}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                        <button
                          type="submit"
                          className="btn btn-primary btn-block"
                          disabled={loading}
                        >
                          {loading ? 'Creating Account...' : 'Create Account'}
                        </button>
                        <div className="text-center mt-4">
                          <p className="text-sm text-gray-600">
                            Already have an account?{' '}
                            <button
                              type="button"
                              onClick={() => setShowLogin(true)}
                              className="text-primary hover:text-primary-dark font-medium"
                            >
                              Sign In
                            </button>
                          </p>
                        </div>
                      </form>
                    </>
                  ) : (
                    <>
                      <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-text mb-2">Sign In</h2>
                        <div className="w-full h-px bg-gray-200"></div>
                      </div>

                      {showForgotPassword ? (
                        <>
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-text mb-2">Reset Password</h2>
                          </div>
                          <form onSubmit={handleResetPassword}>
                            <input
                              type="email"
                              placeholder="Enter your email address"
                              className="form-control mb-4"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              required
                            />
                            <button
                              type="submit"
                              className="btn btn-primary btn-block mb-4"
                              disabled={loading}
                            >
                              {loading ? 'Sending...' : 'Send Reset Link'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-simple btn-block"
                              onClick={() => setShowForgotPassword(false)}
                            >
                              Back to Login
                            </button>
                          </form>
                        </>
                      ) : (
                        <form onSubmit={handleLogin}>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            className="form-control mb-4"
                            value={loginData.email}
                            onChange={handleLoginChange}
                            required
                          />
                          <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            className="form-control mb-4"
                            value={loginData.password}
                            onChange={handleLoginChange}
                            required
                          />
                          <button
                            type="submit"
                            className="btn btn-primary btn-block mb-4"
                            disabled={loading}
                          >
                            {loading ? 'Signing In...' : 'Sign In'}
                          </button>
                          <div className="text-center mb-4">
                            <button
                              type="button"
                              className="btn btn-simple btn-block"
                              onClick={() => setShowForgotPassword(true)}
                            >
                              Forgot Password?
                            </button>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">
                              Don't have an account?{' '}
                              <button
                                type="button"
                                onClick={() => setShowLogin(false)}
                                className="text-primary hover:text-primary-dark font-medium"
                              >
                                Create Account
                              </button>
                            </p>
                          </div>
                        </form>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content Section - PHP Site Style */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-text mb-4">
              Start Your Journey
            </h2>
            <p className="text-xl text-text-muted max-w-2xl mx-auto">
              Find meaningful connections and lasting relationships.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
// Fixed JSX syntax errors